import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../global/services/json-object-factory.service';
import { FtcAttributeLockUnlockService } from './ftc-attribute-lock-unlock.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-ftc-attribute-lock-unlock',
    templateUrl: './ftc-attribute-lock-unlock.component.html',
    styleUrls: ['./ftc-attribute-lock-unlock.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FtcAttributeLockUnlockComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  GENERAL_CONFIG: any = {};
  private globalParams: any = {};
  filterParams: any = {};
  private userSettings: any = {};

  minimise = false;
  isSaveClicked = false;

  logged_in_user = '';
  curentyear = '';
  scenario = '';
  jcd_key: any;

  mode: 'lock' | 'unlock' = 'lock';
  lockUnlockSelected: any[] = [];
  warningExists = false;
  warningMessage: string | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private lockUnlockService: FtcAttributeLockUnlockService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalParams = header.globalParams || {};
    this.userSettings = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const callbackName = activeScreen?.data?.modalName
      || activeScreen?.data?.modal_name
      || activeScreen?.data?.colData?.call_back
      || '';
    this.mode = callbackName.toLowerCase().includes('unlock') ? 'unlock' : 'lock';

    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    if (gridPayload && Array.isArray(gridPayload)) {
      this.lockUnlockSelected = [];
      for (const item of gridPayload) {
        const row = item?.data ?? item;
        if (!row) continue;

        if (this.mode === 'lock') {
          row.LOCK_STATUS = row.STATUS_FLAG === 'Locked' ? 'Already Locked' : '';
        } else {
          row.UNLOCK_STATUS = row.STATUS_FLAG === 'Unlocked' ? 'Already Unlocked' : '';
        }
        this.lockUnlockSelected.push(row);
      }
    }
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;

    this.logged_in_user = this.userSettings?.user?.sso_id || this.userSettings?.sso_id || '';
    this.curentyear = this.filterParams.tax_year;
    this.scenario = this.filterParams.scenario;
    this.jcd_key = this.globalParams.jcd_key;

    this.checkReturnFiled();
    this.computeWarnings();
  }

  private checkReturnFiled(): void {
    if (this.lockUnlockSelected.length > 0) {
      const isReturnFiled = this.lockUnlockSelected[0]?.IS_RETURN_FILED;
      if (isReturnFiled === 'Y') {
        this.gtwSnackbar.openSnackBar('Tax Return has already been filed for tax year ' + this.curentyear + '.', 'gtw-snackbar--red', true, 4);
        this.cancel();
        return;
      }
    }
  }

  computeWarnings(): void {
    this.warningExists = false;
    this.warningMessage = null;

    const targetState = this.mode === 'lock' ? 'Locked' : 'Unlocked';
    for (const row of this.lockUnlockSelected) {
      if (row.STATUS_FLAG === targetState) {
        this.warningExists = true;
        break;
      }
    }

    if (this.warningExists) {
      this.warningMessage = 'Please resolve the warnings before Save';
    }
    this.cdr.detectChanges();
  }

  removeLockUnLockRow(row: any): void {
    const index = this.lockUnlockSelected.indexOf(row);
    if (index > -1) {
      this.lockUnlockSelected.splice(index, 1);
    }
    this.computeWarnings();
  }

  isRowWarning(row: any): boolean {
    if (this.mode === 'lock') {
      return row.STATUS_FLAG === 'Locked';
    }
    return row.STATUS_FLAG === 'Unlocked';
  }

  getRowWarningText(row: any): string {
    if (this.mode === 'lock' && row.STATUS_FLAG === 'Locked') {
      return 'Already Locked';
    }
    if (this.mode === 'unlock' && row.STATUS_FLAG === 'Unlocked') {
      return 'Already Unlocked';
    }
    return '';
  }

  save(): void {
    this.isSaveClicked = true;
    if (this.lockUnlockSelected.length === 0) {
      this.isSaveClicked = false;
      this.gtwSnackbar.openSnackBar('Please select at least one row', 'gtw-snackbar--red', true, 4);
      return;
    }

    const skipState = this.mode === 'lock' ? 'Locked' : 'Unlocked';
    let allTagKeys = '';
    for (const row of this.lockUnlockSelected) {
      if (row.STATUS_FLAG !== skipState) {
        allTagKeys += (allTagKeys ? ',' : '') + row.TAG_KEY;
      }
    }

    const lockFlag = this.mode === 'lock' ? 'L' : 'U';
    const message = this.mode === 'lock'
      ? 'Selected Tags has been locked.'
      : 'Selected Tags has been unlocked.';

    this.lockUnlockService.saveLockAndUnLocking(
      this.GENERAL_CONFIG.base_url,
      this.globalParams,
      allTagKeys,
      lockFlag,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.data?.errorMessage && result.data.errorMessage !== 'null') {
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar(result.data.errorMessage, 'gtw-snackbar--red', true, 4);
      } else {
        this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--green', true, 4);
        this.requestGridUpdate.emit('refresh');
      }
      this.cdr.detectChanges();
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
